import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { DocumentHistoryEntry } from "@/domains/documents/history/documentHistory";
import type { useDocumentHistory } from "@/domains/documents/hooks/useDocumentHistory";
import {
  ALL_HISTORY_ACTORS,
  ALL_HISTORY_FILE_TYPES,
  availableDocumentHistoryActors,
  availableDocumentHistoryFileTypes,
  availableDocumentHistoryFilters,
  documentHistoryDayKey,
  filterDocumentHistoryEntries,
  msUntilNextLocalDay,
  type DocumentHistoryDateFilter as DateFilterValue,
  type DocumentHistoryFilter,
  type DocumentHistoryRefinements,
} from "@/domains/documents/history/documentHistoryPresentation";

type DocumentHistoryQuery = ReturnType<typeof useDocumentHistory>;

/**
 * The local calendar day, re-rendering at each midnight. Date presets resolve
 * against the clock, and the filtered list is memoised — without a dependency
 * that changes when the day does, "Today" left open overnight would keep
 * showing yesterday.
 */
function currentLocalDay(): string {
  return documentHistoryDayKey(new Date());
}

function useLocalDayKey(enabled: boolean): string {
  const subscribe = useCallback(
    (notify: () => void) => {
      let timer: number | undefined;
      const arm = () => {
        timer = window.setTimeout(() => {
          notify();
          arm();
        }, msUntilNextLocalDay());
      };
      if (enabled) arm();
      return () => window.clearTimeout(timer);
    },
    [enabled]
  );
  return useSyncExternalStore(subscribe, currentLocalDay, currentLocalDay);
}

/**
 * A selection whose option is no longer offered is reset, not just hidden:
 * filtering on it would narrow by something the control cannot show, and
 * keeping it in state would silently re-engage it once the option came back.
 */
function useResetWhenUnavailable<T>(selected: T, effective: T, set: (value: T) => void) {
  useEffect(() => {
    if (selected !== effective) set(effective);
  }, [selected, effective, set]);
}

/**
 * The panel stays mounted while the user moves between projects, so filters
 * chosen for one scope would otherwise apply to the next — a date range or a
 * search can make a different project's history look empty. Person, file-type
 * and event selections already reset when their options leave; this covers
 * the search text and the date filter, which have no options to leave.
 */
export function useClearOnScopeChange(
  buildingId: string,
  projectId: number | null,
  clear: () => void
) {
  const clearRef = useRef(clear);
  useEffect(() => {
    clearRef.current = clear;
  });
  const scope = `${buildingId}:${projectId ?? "building"}`;
  const previousScope = useRef(scope);
  useEffect(() => {
    if (previousScope.current === scope) return;
    previousScope.current = scope;
    clearRef.current();
  }, [scope]);
}

function useFilteredHistory(
  entries: DocumentHistoryEntry[],
  query: string,
  filter: DocumentHistoryFilter,
  { actorKeys, fileType, dateFilter }: DocumentHistoryRefinements,
  dayKey: string
) {
  return useMemo(
    () =>
      filterDocumentHistoryEntries(
        entries,
        query,
        filter,
        { actorKeys, fileType, dateFilter },
        new Date(`${dayKey}T00:00:00`)
      ),
    [entries, query, filter, actorKeys, fileType, dateFilter, dayKey]
  );
}

function useFilterSelection() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<DocumentHistoryFilter>("all");
  const [actor, setActor] = useState<string>(ALL_HISTORY_ACTORS);
  const [fileType, setFileType] = useState<string>(ALL_HISTORY_FILE_TYPES);
  const [dateFilter, setDateFilter] = useState<DateFilterValue | null>(null);
  const clear = () => {
    setQuery("");
    setFilter("all");
    setActor(ALL_HISTORY_ACTORS);
    setFileType(ALL_HISTORY_FILE_TYPES);
    setDateFilter(null);
  };
  return {
    query,
    setQuery,
    filter,
    setFilter,
    actor,
    setActor,
    fileType,
    setFileType,
    dateFilter,
    setDateFilter,
    clear,
  };
}

function useHistoryChoices(entries: DocumentHistoryEntry[], language: string) {
  const availableFilters = useMemo(() => availableDocumentHistoryFilters(entries), [entries]);
  const availableActors = useMemo(
    () => availableDocumentHistoryActors(entries, language),
    [entries, language]
  );
  const availableFileTypes = useMemo(() => availableDocumentHistoryFileTypes(entries), [entries]);
  return { availableFilters, availableActors, availableFileTypes };
}

function useAvailableSelection(
  selection: ReturnType<typeof useFilterSelection>,
  choices: ReturnType<typeof useHistoryChoices>
) {
  const filter = choices.availableFilters.includes(selection.filter) ? selection.filter : "all";
  const actor = choices.availableActors.some((option) => option.key === selection.actor)
    ? selection.actor
    : ALL_HISTORY_ACTORS;
  const fileType = choices.availableFileTypes.includes(selection.fileType)
    ? selection.fileType
    : ALL_HISTORY_FILE_TYPES;
  useResetWhenUnavailable(selection.filter, filter, selection.setFilter);
  useResetWhenUnavailable(selection.actor, actor, selection.setActor);
  useResetWhenUnavailable(selection.fileType, fileType, selection.setFileType);
  return { filter, actor, fileType };
}

// Every choice list is derived from the history loaded so far.
export function useHistoryFilters(history: DocumentHistoryQuery, language: string, open: boolean) {
  const selection = useFilterSelection();
  const entries = useMemo(
    () => history.data?.pages.flatMap((page) => page.entries) ?? [],
    [history.data]
  );
  const choices = useHistoryChoices(entries, language);
  const effective = useAvailableSelection(selection, choices);
  const { query, dateFilter } = selection;
  const { filter, actor, fileType } = effective;
  const dayKey = useLocalDayKey(open);
  const filteredEntries = useFilteredHistory(
    entries,
    query,
    filter,
    {
      actorKeys: choices.availableActors.find((option) => option.key === actor)?.memberKeys,
      fileType,
      dateFilter,
    },
    dayKey
  );
  const hasFilters =
    query.trim().length > 0 ||
    filter !== "all" ||
    actor !== ALL_HISTORY_ACTORS ||
    fileType !== ALL_HISTORY_FILE_TYPES ||
    dateFilter !== null;
  return { ...selection, ...choices, ...effective, entries, filteredEntries, hasFilters };
}
